import {
  $isElementNode,
  $isRangeSelection,
  BaseSelection,
  ElementNode,
  RangeSelection,
  TextNode
} from 'lexical';
import { $isHeadingNode, $isQuoteNode } from '@lexical/rich-text';
import { $isListNode, ListNode } from '@lexical/list';
import { $isLinkNode } from '@lexical/link';
import { $getNearestNodeOfType } from '@lexical/utils';
import { BlockType, EditorFormatState } from '../types';

/**
 * Get the top level element from a node
 */
export function getTopLevelElement(
  node: ElementNode | TextNode
): ElementNode | TextNode {
  return node.getKey() === 'root' ? node : node.getTopLevelElementOrThrow();
}

/**
 * Update text format states (bold, italic, etc.)
 */
export function updateTextFormatState(
  selection: BaseSelection,
  formatState: EditorFormatState
): void {
  if (!$isRangeSelection(selection)) return;

  formatState.isBold = selection.hasFormat('bold');
  formatState.isItalic = selection.hasFormat('italic');
  formatState.isStrikethrough = selection.hasFormat('strikethrough');
  formatState.isCode = selection.hasFormat('code');
  formatState.isUnderline = selection.hasFormat('underline');
}

/**
 * Check if selection contains a link node
 */
export function checkForLinkNode(selection: BaseSelection): boolean {
  const nodes = selection.getNodes();
  for (const node of nodes) {
    if (
      $isLinkNode(node) ||
      (node.getParent() && $isLinkNode(node.getParent()))
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Determine the block type of the current selection
 */
export function determineBlockType(
  element: ElementNode | TextNode,
  anchorNode: ElementNode | TextNode
): BlockType {
  if ($isHeadingNode(element)) {
    return element.getTag() as BlockType;
  } else if ($isListNode(element)) {
    return determineListType(anchorNode, element) as BlockType;
  } else if ($isQuoteNode(element)) {
    return 'quote';
  } else {
    return 'paragraph';
  }
}

/**
 * Determine the list type (bullet or number)
 */
export function determineListType(
  anchorNode: ElementNode | TextNode,
  element: ElementNode | TextNode
): string {
  const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
  return parentList
    ? parentList.getListType()
    : (element as ListNode).getListType();
}

/**
 * Find URL of existing link in selection
 */
export function findExistingLinkUrl(selection: BaseSelection): string {
  const nodes = selection.getNodes();

  for (const node of nodes) {
    const linkNode = $isLinkNode(node)
      ? node
      : $isLinkNode(node.getParent())
        ? node.getParent()
        : null;

    if ($isLinkNode(linkNode)) {
      return linkNode.getURL() || 'https://';
    }
  }

  return 'https://';
}

/**
 * Checks if the selection contains any element nodes
 *
 * @param selection - The range selection to check
 * @returns True if the selection contains element nodes
 */
export const containsElementNodes = (selection: RangeSelection): boolean =>
  selection.getNodes().some(node => $isElementNode(node));
